import { AnimatePresence, motion } from 'motion/react'
import { useCallback, useId } from 'react'
import { LoadingSpinner } from '@/components/loading-spinner'
import { IGooglePlacesSelectOption } from '@/features/google-places/google-places-types'
import { SvgGenericMapNeedle } from '@/icons/svg-generic-map-needle'
import { IOnClickEvent } from '@/typings/onclick-event'
import { run } from '@/utils/run'
import { cn } from '@/utils/styles/cn'
import { COLORS } from '@/utils/styles/colors'

interface IGooglePlacesOptionProps {
    option: IGooglePlacesSelectOption
    onClick?: () => void | Promise<unknown>
    setIsLoading?: (isLoading: boolean) => void
    isLoading?: boolean
    highlighted?: boolean
}

export const GooglePlacesOption = ({
    option,
    onClick: propsOnClick,
    isLoading,
    setIsLoading,
    highlighted,
}: IGooglePlacesOptionProps) => {
    const componentId = useId()

    const onClick = useCallback(
        (e: IOnClickEvent) => {
            e.preventDefault()
            e.stopPropagation()

            const promise = propsOnClick?.()

            if (promise) {
                setIsLoading?.(true)
                promise.finally(() => setIsLoading?.(false))
            }
        },
        [propsOnClick, setIsLoading]
    )

    return (
        <button
            onClick={onClick}
            className={cn(
                'flex items-center  pl-3 pr-8 py-3 gap-x-2 bg-white webkit-tap-transparent relative w-full cursor-pointer select-none hover:bg-black/5 text-left',
                highlighted && 'bg-black/10'
            )}
        >
            <div className="min-w-[20px] min-h-[20px] flex items-center justify-center">
                <SvgGenericMapNeedle color={COLORS.black} size={16} />
            </div>

            <p className="font-medium text-[14px] leading-[135%]">{option.label}</p>

            <AnimatePresence>
                <motion.div
                    key={`address-display-option-right-container--${isLoading}--${componentId}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center justify-center absolute right-4 transform-center-y"
                >
                    {run(() => {
                        if (isLoading) {
                            return <LoadingSpinner color="black" size={20} />
                        }

                        return null
                    })}
                </motion.div>
            </AnimatePresence>
        </button>
    )
}
